import type { ClientMessage, ServerMessage } from "@benji/sim-core";

type Handler = (msg: ServerMessage) => void;

/**
 * Thin typed WebSocket wrapper for the authoritative game server.
 *
 * - Buffers outgoing messages until the socket is OPEN.
 * - Surfaces every server message through one handler (the orchestrator
 *   switches on `msg.t`).
 * - Never throws on transport failure: `onClose` lets the client fall back to
 *   an offline local game so it still runs without a backend.
 */
export class NetClient {
  private ws?: WebSocket;
  private queue: string[] = [];
  private handler?: Handler;
  private openCb?: () => void;
  private closeCb?: () => void;
  private opened = false;

  constructor(private readonly url = defaultWsUrl()) {}

  onMessage(h: Handler) { this.handler = h; }
  onOpen(cb: () => void) { this.openCb = cb; }
  onClose(cb: () => void) { this.closeCb = cb; }

  get isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  connect() {
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch {
      this.closeCb?.();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.opened = true;
      for (const m of this.queue) ws.send(m);
      this.queue = [];
      this.openCb?.();
    };
    ws.onmessage = (ev) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(String(ev.data)) as ServerMessage;
      } catch {
        return;
      }
      this.handler?.(msg);
    };
    ws.onerror = () => {
      if (!this.opened) this.closeCb?.();
    };
    ws.onclose = () => this.closeCb?.();
  }

  send(msg: ClientMessage) {
    const data = JSON.stringify(msg);
    if (this.isOpen) this.ws!.send(data);
    else this.queue.push(data);
  }
}

function defaultWsUrl(): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}/ws`;
}

/** Stable per-browser player id (placeholder until real auth/KYC). */
export function playerToken(): string {
  const KEY = "benji_pid";
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = "p_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(KEY, id);
  }
  return id;
}

export function randomClientSeed(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}
